<script setup lang="ts">
/**
 * AIAssistantReviewView.vue - Review State Wrapper
 * 
 * Wrapper for the review_ready state that handles widget/modal positioning.
 * Contains AIAssistantSummary internally for the actual review UI.
 * 
 * Features:
 * - Widget mode: positioned bottom-right corner
 * - Modal mode: centered on screen
 * - Header with expand/minimize/close buttons
 * - Passes events up to parent orchestrator
 */
import { nextTick } from "vue";
import AIAssistantSummary from "./AIAssistantSummary.vue";

const props = defineProps({
  sessionData: {
    type: Object,
    required: true,
  },
  isMinimized: {
    type: Boolean,
    default: false,
  },
  displayMode: {
    type: String as () => "widget" | "modal",
    default: "widget",
  },
  position: {
    type: String as () => "left" | "right",
    default: "right",
  },
});

const emit = defineEmits([
  "expand",
  "minimize",
  "collapse",
  "review-saved",
  "session-discarded",
]);

const onExpand = () => {
  emit("expand");
};

const onMinimize = () => {
  emit("minimize");
};

const onCollapse = () => {
  emit("collapse");
};

const onReviewSaved = () => {
  emit("review-saved");
};

// Use nextTick to prevent "emitsOptions" error when component unmounts during emit
const onSessionDiscarded = () => {
  nextTick(() => {
    emit("session-discarded");
  });
};
</script>

<template>
  <div class="review-view-container">
    <!-- Backdrop is now handled by parent container's ::after pseudo-element -->
    
    <div class="review-view" :class="[displayMode, position]"
    >
      <!-- Header with Watson components -->
      <div class="review-view-header">
        <div class="header-title">
          <w-icon name="smart" :size="24" class="noa-icon" />
          <span class="review-title">NOA Notes</span>
        </div>
        
        <div class="header-actions">
          <!-- Widget mode: minimize (hide) + expand (to modal) -->
          <w-icon-button
            v-if="displayMode === 'widget'"
            icon-name="minus"
            type="button"
            class="icon-actions"
            @click="onMinimize"
          />
          <w-icon-button
            v-if="displayMode === 'widget'"
            icon-name="open-in-full"
            type="button"
            class="icon-actions"
            @click="onExpand"
          />
          <!-- Modal mode: minimize (hide) + collapse (to widget) -->
          <w-icon-button
            v-if="displayMode === 'modal'"
            icon-name="minus"
            type="button"
            class="icon-actions"
            @click="onMinimize"
          />
          <w-icon-button
            v-if="displayMode === 'modal'"
            icon-name="close-full"
            type="button"
            class="icon-actions"
            @click="onCollapse"
          />
        </div>
      </div>
      
      <!-- Summary content -->
      <div class="review-view-content">
        <AIAssistantSummary
          :initial-context="sessionData"
          :parent-summary-status="'ready'"
          @review-saved="onReviewSaved"
          @session-discarded="onSessionDiscarded"
          @close="onMinimize"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.review-view-container {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9999999;
}


.review-view {
  background: var(--w-modal-color-background);
  border-radius: 16px;
  box-shadow: var(--w-popover-popout-box-shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
  z-index: 2;

  // Widget mode - positioned in corner
  &.widget {
    position: fixed;
    bottom: 80px;
    width: 420px;
    height: 475px;

    &.right {
      right: 30px;
    }

    &.left {
      left: 30px;
    }
  }

  // Modal mode - centered on screen
  &.modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    height: 70%;
    max-width: 960px;
    max-height: 90vh;
  }
}


.review-view-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--w-border-color-default, #e0e0e0);
  flex-shrink: 0;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 8px;

  .w-icon,
  ::v-deep(.w-icon) {
    fill: #03c3a5;
  }
}

.review-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--w-text-color-primary);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.icon-actions::v-deep(.w-icon) {
  fill: #565f5f;
}

.review-view-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  :deep(.ai-assistant-container) {
    height: 100%;
    display: flex;
    flex-direction: column;

    .content-wrapper {
      flex: 1;
      overflow: hidden;
      overflow-y: auto;
      min-height: 0;
    }

    .chat-footer {
      position: relative;
      flex-shrink: 0;
      margin-top: auto;
    }
  }
}
</style>
